import type { Move, Premove, Square } from '@draughts/shared';
import { squareToSVGCoords } from './board-coordinates';

/**
 * The action the board should take when a player clicks or drops on `square`
 * while a piece is selected.
 *
 * - `simple`            — a quiet move or single jump: execute it immediately.
 * - `capture-direct`    — the square is the unique FINAL landing of a multi-capture:
 *                         play the whole sequence at once (animated).
 * - `capture-sequential`— start the step-by-step capture from `startMove` (the
 *                         player clicked a first hop, or chose an ambiguous final
 *                         that must be disambiguated jump by jump).
 * - `none`              — the square is not a valid destination.
 */
export type MoveIntent =
  | { kind: 'simple'; move: Move }
  | { kind: 'capture-direct'; move: Move }
  | { kind: 'capture-sequential'; startMove: Move }
  | { kind: 'none' };

function isMultiCapture(move: Move): boolean {
  return !!(move.path && move.path.length > 1);
}

/**
 * Map a clicked/dropped square to the intended move, supporting BOTH input styles:
 * stepping through a capture jump-by-jump, or selecting the final square to play
 * the entire capture in one action.
 *
 * Resolution order (first match wins):
 *  1. A quiet move / single jump landing on the square.
 *  2. A multi-capture whose unique FINAL square is this one → execute directly.
 *  3. A multi-capture whose FIRST hop is this square → start the sequence.
 *  4. Nothing else → the square is not a destination.
 *
 * L'ordine fra il 2 e il 3 è una scelta: un arrivo unico VINCE su un primo salto,
 * perché l'anello che lo segna promette «qui concludi» (vedi il test omonimo).
 *
 * `'none'` resta solo dove la casella non è NÉ un arrivo unico NÉ un primo salto —
 * cioè esattamente dove la damiera non le disegna nessun segno. È l'invariante che
 * tiene in piedi tutto l'inserimento delle mosse: ogni pallino e ogni anello fanno
 * quello che promettono, e non esiste nessuna casella segnata che non agisce.
 * La prova è in `move-input.segni-vivi.test.ts`, su tutte le varianti.
 */
export function resolveMoveIntent(legalMoves: Move[], square: Square): MoveIntent {
  // 1. Quiet move or single jump (these never carry a multi-step `path`).
  const simple = legalMoves.find((m) => !isMultiCapture(m) && m.to === square);
  if (simple) return { kind: 'simple', move: simple };

  const multi = legalMoves.filter(isMultiCapture);

  // 2. Exactly one multi-capture ends here → play it whole (this is the ring).
  const finals = multi.filter((m) => m.to === square);
  if (finals.length === 1) return { kind: 'capture-direct', move: finals[0]! };

  /*
   * 3. Primo salto: si avvia la sequenza, e la scelta resta a chi gioca.
   *
   * Non si sceglie al posto suo nemmeno quando più linee finiscono qui: `makeMove`
   * ri-espande il primo salto in TUTTE le linee che lo condividono (vedi
   * `game-store`), quindi partire non esclude niente — la scelta si fa al salto
   * dopo, dove ogni linea viva ha il suo pallino.
   *
   * Questo ramo stava DOPO un `finals.length > 1 → none`, e per una presa ad
   * ANELLO quel ritorno anticipato era un blocco totale: l'anello ha `path[0] ===
   * to`, quindi l'unica casella toccabile era insieme l'arrivo condiviso dalle due
   * percorrenze e il loro primo salto, il terzo ramo non veniva mai raggiunto, e il
   * pezzo si deselezionava in silenzio. Nella frisone (pedine che mangiano in 8
   * direzioni) succedeva nello 0,36% delle posizioni, e lì non c'era NESSUNA mossa
   * giocabile: si perdeva al tempo. Lo segnalò un utente, nell'ottobre del 2026.
   *
   * La ragione del vecchio ritorno — «non scegliere per chi gioca» — resta
   * onorata: un arrivo condiviso che non è anche un primo salto è ancora inerte,
   * perché non porta l'anello e non ha mai promesso di concludere lì.
   */
  const firstHop = multi.find((m) => m.path![0] === square);
  if (firstHop) return { kind: 'capture-sequential', startMove: firstHop };

  return { kind: 'none' };
}

/**
 * The action the board should take when a player clicks/drops on `square` while a
 * multi-capture is already in progress (a piece has jumped at least once and more
 * jumps remain).
 *
 * - `step`            — the square is the next hop of some still-possible line: advance
 *                       one jump (executeStep).
 * - `finish`          — the square is the FINAL landing of exactly one remaining line
 *                       (same uniqueness rule that draws the ring): play out the rest of
 *                       that line. `remaining` lists the landing squares still to play,
 *                       including the next hop and the final, so executeStep disambiguates
 *                       the line jump by jump.
 * - `none`            — the square is neither a next hop nor a unique final.
 */
export type CaptureStepIntent =
  | { kind: 'step' }
  | { kind: 'finish'; remaining: Square[] }
  | { kind: 'none' };

/**
 * Resolve a click/drop on `square` during an in-progress multi-capture.
 *
 * Resolution order (first match wins):
 *  1. `square` is the next hop (`path[currentStep]`) of any still-possible line → `step`.
 *  2. `square` is the final landing of EXACTLY one remaining line → `finish` (auto-play
 *     the rest of that line). This mirrors the ring's visibility rule (`count === 1`), so
 *     a click on the ring always completes the line it marks. Checking `step` first means
 *     that at the last hop (final === next hop) we take the single-step path, not the timer.
 *  3. Otherwise → `none`.
 */
export function resolveCaptureStep(
  possibleMoves: Move[],
  currentStep: number,
  square: Square,
): CaptureStepIntent {
  // 1. Next hop of any still-possible line.
  const isNextHop = possibleMoves.some(
    (m) => m.path && currentStep < m.path.length && m.path[currentStep] === square,
  );
  if (isNextHop) return { kind: 'step' };

  // 2. Final landing of exactly one remaining line (matches ring visibility).
  const finals = possibleMoves.filter(
    (m) => m.path && currentStep < m.path.length && m.to === square,
  );
  if (finals.length === 1) {
    return { kind: 'finish', remaining: finals[0]!.path!.slice(currentStep) };
  }

  return { kind: 'none' };
}

/**
 * True when the legal move fulfils a queued `premove`. A pre-move only carries intent —
 * a (from, to) pair — so captures are NOT compared: the actual captured pieces are
 * determined by the real board when the turn flips (e.g. an anticipatory recapture queued
 * before the enemy is even jumpable). In draughts a (from, to) pair identifies the geometry
 * uniquely for a single piece (quiet = one step, capture = a landing beyond the enemy/chain
 * of jumps), so this is unambiguous.
 */
export function premoveMatchesMove(legal: Move, premove: Premove): boolean {
  return legal.from === premove.from && legal.to === premove.to;
}

/**
 * Find the legal move that fulfils a queued `premove`, or `null` if the pre-move can't be
 * played in the current position (so it should be discarded). Returns the canonical legal
 * move — with the correct `path` for the NEW position — to execute.
 */
export function findMatchingMove(legalMoves: Move[], premove: Premove): Move | null {
  return legalMoves.find((m) => premoveMatchesMove(m, premove)) ?? null;
}

/**
 * Squares that should be highlighted as "direct capture" targets (a ring): the
 * landing square of ANY capture (single jump or the final of a multi-capture),
 * but only when that landing uniquely identifies one capture line (otherwise it
 * can't be executed in one click — the player must disambiguate first).
 */
export function captureTargetSquares(legalMoves: Move[]): Set<Square> {
  const finalCounts = new Map<Square, number>();
  for (const m of legalMoves) {
    if (m.captures.length > 0) finalCounts.set(m.to, (finalCounts.get(m.to) ?? 0) + 1);
  }
  const targets = new Set<Square>();
  for (const [to, count] of finalCounts) {
    if (count === 1) targets.add(to);
  }
  return targets;
}

/**
 * Gli arrivi di presa di una selezione da PRE-MOSSA (l'anello): il `to` di ogni mossa che
 * cattura, senza la regola dell'unicità che vale per `captureTargetSquares`.
 *
 * L'unicità serve al turno vero, dove un tocco deve eseguire UNA linea sola: un arrivo
 * condiviso da due catene non può essere giocato con un tocco, quindi non porta l'anello.
 * Su una pre-mossa non si esegue niente — si accoda un `(from, to)` e la linea vera la
 * risolve `findMatchingMove` al cambio turno — quindi un arrivo condiviso resterebbe un
 * pallino piatto su una casella dove si mangia: esattamente la confusione fra mossa quieta
 * e presa che i suggerimenti devono togliere.
 */
export function premoveCaptureLandings(moves: Move[]): Set<Square> {
  const targets = new Set<Square>();
  for (const m of moves) {
    if (m.captures.length > 0) targets.add(m.to);
  }
  return targets;
}

/** Board geometry needed to snap an imprecise drop point to a legal destination. */
export interface DropGeom {
  squareSize: number;
  boardSize: number;
  darkSquareBottomRight: boolean;
  allSquares: boolean;
  /** Max distance (SVG units) from a candidate's centre to still snap to it. */
  snapRadius: number;
}

/**
 * The squares the board highlights as destinations for the selected piece, and the
 * ONLY squares `resolveMoveIntent` resolves to a real move: a simple/first-hop
 * destination per move, plus the unique final landing of each capture line. Kept in
 * sync with the highlight computation in GameBoard.
 */
function dropCandidateSquares(legalMoves: Move[]): Square[] {
  const candidates = new Set<Square>();
  for (const m of legalMoves) {
    const first = m.path && m.path.length > 1 ? m.path[0] : undefined;
    candidates.add(first ?? m.to);
  }
  for (const sq of captureTargetSquares(legalMoves)) candidates.add(sq);
  return [...candidates];
}

/**
 * Pick the candidate whose SVG centre is nearest to (x, y). Candidates are visited in
 * ascending square order and the running best is replaced only on a STRICTLY smaller
 * distance, so an exact tie resolves deterministically to the lowest square number.
 * Distances are compared squared (no `Math.sqrt`). Returns null for no candidates.
 */
function nearestCandidate(
  x: number,
  y: number,
  candidates: Square[],
  geom: DropGeom,
): { square: Square; distSq: number } | null {
  let best: Square | null = null;
  let bestDistSq = Infinity;
  for (const sq of [...candidates].sort((a, b) => a - b)) {
    const c = squareToSVGCoords(sq, geom.squareSize, geom.boardSize, geom.darkSquareBottomRight, geom.allSquares);
    const dx = x - c.x;
    const dy = y - c.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = sq;
    }
  }
  return best === null ? null : { square: best, distSq: bestDistSq };
}

/**
 * True when (x, y) lies within the board's pixel box expanded by half a square — i.e.
 * on or just past the edge. A forced move still completes for such near-edge drops,
 * while a clearly off-board drop cancels the drag (so you can abort by dragging away).
 */
function isNearBoard(x: number, y: number, geom: DropGeom): boolean {
  const boardPixelSize = geom.boardSize * geom.squareSize;
  const margin = geom.squareSize / 2;
  return x >= -margin && x <= boardPixelSize + margin && y >= -margin && y <= boardPixelSize + margin;
}

/**
 * Resolve an imprecise drag-drop at SVG point (x, y) to the intended destination
 * square, so a move still lands when the pointer is released off the exact target
 * cell. Returns the square to feed to `resolveMoveIntent` / `executeIntent`, or null
 * when the drop is too far to be a plausible destination (the caller cancels the drag).
 *
 * - Forced move (the piece has a SINGLE legal move): the final landing is returned for
 *   any on/near-board drop, so even a sloppy release completes it — a forced
 *   multi-capture auto-plays whole via its final square (`capture-direct`). A clearly
 *   off-board drop returns null, preserving "drag off the board to abort".
 * - Several moves: snap to the NEAREST highlighted destination, but only within
 *   `snapRadius` of its centre; otherwise null.
 */
export function resolveDropSquare(
  x: number,
  y: number,
  legalMoves: Move[],
  geom: DropGeom,
): Square | null {
  if (legalMoves.length === 0) return null;
  if (legalMoves.length === 1) {
    return isNearBoard(x, y, geom) ? legalMoves[0]!.to : null;
  }
  const nearest = nearestCandidate(x, y, dropCandidateSquares(legalMoves), geom);
  if (nearest === null) return null;
  return nearest.distSq <= geom.snapRadius * geom.snapRadius ? nearest.square : null;
}

/**
 * The in-progress-capture analogue of `resolveDropSquare`: snap an imprecise drop to
 * the intended next square while a multi-capture is being stepped out. Candidates are
 * the squares highlighted mid-sequence — the next hop of every still-possible line,
 * plus each line's unique final landing — matching the highlight rule in GameBoard.
 * Returns the square to pass to `resolveCaptureStep`, or null when the drop is too far.
 *
 * A single next hop (the only continuation) snaps for any on/near-board drop; with
 * several possible next hops the drop must fall within `snapRadius` of one.
 */
export function resolveCaptureDropSquare(
  x: number,
  y: number,
  possibleMoves: Move[],
  currentStep: number,
  geom: DropGeom,
): Square | null {
  const nextHops = new Set<Square>();
  const finalCounts = new Map<Square, number>();
  for (const m of possibleMoves) {
    if (!m.path || currentStep >= m.path.length) continue;
    const step = m.path[currentStep];
    if (step !== undefined) nextHops.add(step);
    finalCounts.set(m.to, (finalCounts.get(m.to) ?? 0) + 1);
  }
  const candidates = new Set<Square>(nextHops);
  for (const [to, count] of finalCounts) {
    if (count === 1) candidates.add(to);
  }
  const nearest = nearestCandidate(x, y, [...candidates], geom);
  if (nearest === null) return null;
  if (nextHops.size === 1 && isNearBoard(x, y, geom)) return nearest.square;
  return nearest.distSq <= geom.snapRadius * geom.snapRadius ? nearest.square : null;
}
